import { PencilSquare, Plus, Trash } from '@medusajs/icons';
import {
  Button,
  Drawer,
  Input,
  Label,
  Prompt,
  Select,
  Skeleton,
  Text,
  toast,
} from '@pcrm/fe/ui-kit';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { ActionMenu } from '../../../components/action-menu';
import {
  SectionContainer,
  SectionEmpty,
  SectionHeader,
  SectionRow,
} from '../../../components/section';
import {
  createDataSource,
  DATA_SOURCES_QUERY_KEY,
  type DataSource,
  type DataSourceFilter,
  dataSourceOptionsQueryOptions,
  dataSourcesQueryOptions,
  deleteDataSource,
  FILTER_OPERATORS,
  updateDataSource,
} from '../../../features/templates/data-sources.api';

export const Route = createFileRoute('/_authenticated/setari/surse-de-date')({
  component: SurseDeDatePagina,
});

/**
 * Sursele de date pentru listele din template-uri.
 *
 * Un câmp de template e ori text liber, ori **alegere dintr-o listă** — iar lista se
 * declară aici: tabel, coloana de valoare, coloana de etichetă, filtru. Sursă nouă = un
 * rând, fără deploy.
 *
 * ⚠️ **Ecranul poate expune, în principiu, orice coloană din bază.** De asta stă pe
 * `templates.sources`, separat de permisiunea de editare a template-urilor. Serverul
 * verifică tabelul și coloanele în `information_schema` la salvare și citează
 * identificatorii la interogare; ecranul nu e ultima poartă, e prima.
 * Vezi docs/wiki/domains/contract-templates.md
 */
function SurseDeDatePagina() {
  const { data, isPending, error } = useQuery(dataSourcesQueryOptions());
  const [editing, setEditing] = useState<DataSource | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<DataSource | null>(null);
  const [previewing, setPreviewing] = useState<DataSource | null>(null);
  const queryClient = useQueryClient();

  const sterge = useMutation({
    mutationFn: (id: string) => deleteDataSource(id),
    onSuccess: async () => {
      toast.success('Sursă ștearsă');
      await queryClient.invalidateQueries({ queryKey: DATA_SOURCES_QUERY_KEY });
      setDeleting(null);
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Ștergerea a eșuat.'),
  });

  return (
    <>
      <SectionContainer>
        <SectionHeader
          title="Surse de date"
          subtitle="Listele din care se alege la generarea unui document."
          actions={
            <Button size="small" variant="secondary" onClick={() => setCreating(true)}>
              <Plus />
              Adaugă
            </Button>
          }
        />

        {isPending ? (
          <div className="flex flex-col gap-3 px-6 py-4">
            <Skeleton className="h-5 w-full" />
            <Skeleton className="h-5 w-2/3" />
          </div>
        ) : error ? (
          <SectionEmpty tone="error">
            {error instanceof Error ? error.message : 'Sursele nu au putut fi citite.'}
          </SectionEmpty>
        ) : data.length === 0 ? (
          <SectionEmpty>Nicio sursă declarată.</SectionEmpty>
        ) : (
          data.map((source) => (
            <SectionRow
              key={source.id}
              title={source.name}
              value={
                <span className="font-mono text-xs">
                  {source.tableName}.{source.labelColumn}
                  {source.filter === null
                    ? ''
                    : ` · ${source.filter.column} ${source.filter.operator} ${source.filter.value}`}
                </span>
              }
              actions={
                <ActionMenu
                  groups={[
                    {
                      actions: [
                        {
                          icon: <PencilSquare />,
                          label: 'Modifică',
                          onClick: () => setEditing(source),
                        },
                        {
                          icon: <Plus />,
                          label: 'Vezi rândurile',
                          onClick: () => setPreviewing(source),
                        },
                      ],
                    },
                    {
                      actions: [
                        {
                          icon: <Trash />,
                          label: 'Șterge',
                          onClick: () => setDeleting(source),
                        },
                      ],
                    },
                  ]}
                />
              }
            />
          ))
        )}
      </SectionContainer>

      <SourceDrawer
        source={editing}
        open={creating || editing !== null}
        onClose={() => {
          setCreating(false);
          setEditing(null);
        }}
      />

      <PreviewDrawer source={previewing} onClose={() => setPreviewing(null)} />

      <Prompt open={deleting !== null} onOpenChange={(next) => !next && setDeleting(null)}>
        <Prompt.Content>
          <Prompt.Header>
            <Prompt.Title>Ștergi sursa „{deleting?.name}"?</Prompt.Title>
            <Prompt.Description>
              Câmpurile care o folosesc rămân fără listă, iar operatorul nu mai are ce alege.
            </Prompt.Description>
          </Prompt.Header>
          <Prompt.Footer>
            <Prompt.Cancel>Renunță</Prompt.Cancel>
            <Prompt.Action onClick={() => deleting && sterge.mutate(deleting.id)}>
              Șterge
            </Prompt.Action>
          </Prompt.Footer>
        </Prompt.Content>
      </Prompt>
    </>
  );
}

/** Același sertar pentru adăugare și pentru modificare. */
function SourceDrawer({
  source,
  open,
  onClose,
}: {
  source: DataSource | null;
  open: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [tableName, setTableName] = useState('');
  const [valueColumn, setValueColumn] = useState('id');
  const [labelColumn, setLabelColumn] = useState('name');
  const [filter, setFilter] = useState<DataSourceFilter | null>(null);

  useEffect(() => {
    if (open) {
      setName(source?.name ?? '');
      setTableName(source?.tableName ?? '');
      setValueColumn(source?.valueColumn ?? 'id');
      setLabelColumn(source?.labelColumn ?? 'name');
      setFilter(source?.filter ?? null);
    }
  }, [open, source]);

  const salveaza = useMutation({
    mutationFn: () => {
      const request = { name, tableName, valueColumn, labelColumn, filter };

      return source ? updateDataSource(source.id, request) : createDataSource(request);
    },
    onSuccess: async () => {
      toast.success(source ? 'Sursă actualizată' : 'Sursă adăugată');
      await queryClient.invalidateQueries({ queryKey: DATA_SOURCES_QUERY_KEY });
      onClose();
    },
    // Mesajul serverului se arată ca atare: el spune care coloană lipsește din tabel, iar
    // un „Salvarea a eșuat" ar fi ascuns exact informația utilă.
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Salvarea a eșuat.'),
  });

  return (
    <Drawer open={open} onOpenChange={(next) => !next && onClose()}>
      <Drawer.Content>
        <Drawer.Header>
          <Drawer.Title>{source ? `Modifică ${source.name}` : 'Adaugă o sursă'}</Drawer.Title>
        </Drawer.Header>

        <Drawer.Body className="flex flex-col gap-y-4">
          <div className="flex flex-col gap-y-2">
            <Label size="small">Nume</Label>
            <Input value={name} onChange={(event) => setName(event.target.value)} />
          </div>

          <div className="flex flex-col gap-y-2">
            <Label size="small">Tabel</Label>
            <Input
              className="font-mono"
              value={tableName}
              onChange={(event) => setTableName(event.target.value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="flex flex-col gap-y-2">
              <Label size="small">Coloana de valoare</Label>
              <Input
                className="font-mono"
                value={valueColumn}
                onChange={(event) => setValueColumn(event.target.value)}
              />
            </div>
            <div className="flex flex-col gap-y-2">
              <Label size="small">Coloana de etichetă</Label>
              <Input
                className="font-mono"
                value={labelColumn}
                onChange={(event) => setLabelColumn(event.target.value)}
              />
            </div>
          </div>

          <div className="flex flex-col gap-y-2">
            <Label size="small">Filtru</Label>
            {filter === null ? (
              <Button
                size="small"
                variant="secondary"
                onClick={() => setFilter({ column: '', operator: '=', value: '' })}
              >
                Adaugă un filtru
              </Button>
            ) : (
              <div className="flex items-center gap-2">
                <Input
                  className="font-mono"
                  placeholder="coloană"
                  value={filter.column}
                  onChange={(event) => setFilter({ ...filter, column: event.target.value })}
                />
                <Select
                  value={filter.operator}
                  onValueChange={(next) =>
                    setFilter({ ...filter, operator: next as DataSourceFilter['operator'] })
                  }
                >
                  <Select.Trigger className="w-24">
                    <Select.Value />
                  </Select.Trigger>
                  <Select.Content>
                    {FILTER_OPERATORS.map((operator) => (
                      <Select.Item key={operator} value={operator}>
                        {operator}
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select>
                <Input
                  placeholder="valoare"
                  value={filter.value}
                  onChange={(event) => setFilter({ ...filter, value: event.target.value })}
                />
                <Button size="small" variant="transparent" onClick={() => setFilter(null)}>
                  <Trash />
                </Button>
              </div>
            )}
          </div>
        </Drawer.Body>

        <Drawer.Footer>
          <Button variant="secondary" onClick={onClose}>
            Renunță
          </Button>
          <Button onClick={() => salveaza.mutate()} disabled={salveaza.isPending}>
            {salveaza.isPending ? 'Se salvează…' : 'Salvează'}
          </Button>
        </Drawer.Footer>
      </Drawer.Content>
    </Drawer>
  );
}

/**
 * Rândurile pe care le va vedea operatorul.
 *
 * Fără proba asta, o sursă greșită se descoperă abia la generarea unui contract — cu un
 * select gol și nicio explicație.
 */
function PreviewDrawer({ source, onClose }: { source: DataSource | null; onClose: () => void }) {
  const options = useQuery({
    ...dataSourceOptionsQueryOptions(source?.id ?? ''),
    enabled: source !== null,
    retry: false,
  });

  return (
    <Drawer open={source !== null} onOpenChange={(next) => !next && onClose()}>
      <Drawer.Content>
        <Drawer.Header>
          <Drawer.Title>Rândurile din „{source?.name}"</Drawer.Title>
        </Drawer.Header>

        <Drawer.Body className="flex flex-col gap-y-2 overflow-y-auto">
          {options.isPending ? (
            <Skeleton className="h-5 w-full" />
          ) : options.error ? (
            <Text size="small" className="text-ui-fg-error">
              {options.error instanceof Error
                ? options.error.message
                : 'Rândurile nu au putut fi citite.'}
            </Text>
          ) : options.data.length === 0 ? (
            <Text size="small" className="text-ui-fg-subtle">
              Niciun rând. Filtrul e prea strâns sau tabelul e gol.
            </Text>
          ) : (
            options.data.map((option) => (
              <div key={option.value} className="flex items-center justify-between gap-4">
                <Text size="small">{option.label}</Text>
                <Text size="small" className="font-mono text-ui-fg-muted">
                  {option.value}
                </Text>
              </div>
            ))
          )}
        </Drawer.Body>
      </Drawer.Content>
    </Drawer>
  );
}
